import { useReducer, useEffect, useCallback } from 'react'
import {
  useShareUpdatePermissionsMutation,
  ShareListItemFragment,
} from 'gqlTypes'
import { useToast } from 'containers/ToastManager'
import { dataIdFromObject } from 'graphql/apolloClient'
import {
  readShareListItemFromCache,
  writeShareListItemToCache,
} from 'modules/shares/graphql/cache'
import { ErrorHandler } from 'modules/tracing'

type PublicAccessLevel = ShareListItemFragment['publicAccessLevel']

type Values = Pick<
  ShareListItemFragment,
  | 'publicCommentsEnabled'
  | 'publicAccessLevel'
  | 'identifier'
  | 'userCanUpdateCommentsEnabled'
>

type State = {
  initialValues: Values
  values: Values
  mutating: boolean
  confirmDialogOpen: boolean
  confirmPrivate: boolean
}

type Action =
  | { type: 'reset' }
  | { type: 'set-level'; payload: { level: PublicAccessLevel } }
  | { type: 'set-comments-enabled'; payload: { enabled: boolean } }
  | { type: 'success'; payload: { values: Values } }
  | { type: 'set-mutating' }
  | { type: 'hide-dialog' }
  | { type: 'confirm-private' }

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'set-mutating':
      return { ...state, mutating: true }
    case 'reset':
      return {
        ...state,
        values: { ...state.initialValues },
        mutating: false,
      }
    case 'success':
      return {
        ...state,
        values: action.payload.values,
        initialValues: action.payload.values,
        mutating: false,
        confirmDialogOpen: false,
        confirmPrivate: false,
      }
    case 'set-level':
      return {
        ...state,
        values: {
          ...state.values,
          publicAccessLevel: action.payload.level,
        },
        confirmDialogOpen: action.payload.level === 'NONE',
      }
    case 'set-comments-enabled':
      return {
        ...state,
        values: {
          ...state.values,
          publicCommentsEnabled: action.payload.enabled,
        },
      }
    case 'hide-dialog':
      return {
        ...state,
        confirmDialogOpen: false,
      }
    case 'confirm-private':
      return {
        ...state,
        confirmPrivate: true,
      }
    default:
      return state
  }
}

interface UsePublicPermissionsProps {
  share: Values
}

const areValuesEqual = (a: Values, b: Values) =>
  a.publicAccessLevel === b.publicAccessLevel &&
  a.publicCommentsEnabled === b.publicCommentsEnabled

export const usePublicPermissions = ({ share }: UsePublicPermissionsProps) => {
  const { showToast } = useToast()

  const [
    { initialValues, values, mutating, confirmDialogOpen, confirmPrivate },
    dispatch,
  ] = useReducer(reducer, {
    initialValues: share,
    values: share,
    mutating: false,
    confirmDialogOpen: false,
    confirmPrivate: false,
  })

  const [
    shareUpdatePermissions,
    { loading },
  ] = useShareUpdatePermissionsMutation({
    update: (cache, { data }) => {
      const shareToUpdate = readShareListItemFromCache({
        cache,
        id: dataIdFromObject(share) as string,
      })
      if (!shareToUpdate) {
        return
      }

      // Updating `isPrivate` for ShareListItemFragment in the cache
      // keeps the shared icon updated in the shares lists
      writeShareListItemToCache({
        cache,
        share: {
          ...shareToUpdate,
          publicAccessLevel:
            data?.shareUpdatePermissions?.publicAccessLevel || 'NONE',
        },
      })
    },
    onCompleted(data) {
      if (!data?.shareUpdatePermissions) {
        ErrorHandler.shouldNeverHappen(
          'Share update workspace permissions mutation should return valid data'
        )
        dispatch({ type: 'reset' })
        return
      }
      dispatch({
        type: 'success',
        payload: { values: data.shareUpdatePermissions },
      })
    },
    onError() {
      dispatch({ type: 'reset' })
      showToast(
        'There was a problem changing the public link permissions. Please try again.',
        'negative'
      )
    },
  })

  const mutate = useCallback(() => {
    dispatch({ type: 'set-mutating' })

    return shareUpdatePermissions({
      variables: {
        identifier: share.identifier,
        permissions: {
          publicCommentsEnabled: Boolean(values.publicCommentsEnabled),
          publicAccessLevel: values.publicAccessLevel,
        },
      },
    })
  }, [share, values, shareUpdatePermissions])

  useEffect(() => {
    if (values.publicAccessLevel === 'NONE' && !confirmPrivate) {
      return
    }

    if (!mutating && !areValuesEqual(values, initialValues)) {
      mutate()
    }
  }, [values, initialValues, mutate, mutating, confirmPrivate])

  const getSelectProps = useCallback(
    () => ({
      loading,
      access: {
        kind: 'public',
        level: initialValues.publicAccessLevel,
        setLevel: (level: PublicAccessLevel) =>
          dispatch({ type: 'set-level', payload: { level } }),
      } as const,
      commentsEnabled: Boolean(values.publicCommentsEnabled),
      setCommentsEnabled: (enabled: boolean) => {
        dispatch({ type: 'set-comments-enabled', payload: { enabled } })
      },
      canUpdateCommentsEnabled: initialValues.userCanUpdateCommentsEnabled,
    }),
    [loading, values, dispatch, initialValues]
  )

  const getConfirmDialogProps = useCallback(
    () => ({
      hideModal() {
        dispatch({
          type: 'hide-dialog',
        })
      },
      onConfirm() {
        dispatch({
          type: 'confirm-private',
        })
      },
      isModalOpen: true,
      confirmButton: {
        text: 'Confirm',
        intent: 'positive',
        loading: mutating,
      } as const,
    }),
    [mutating]
  )

  return {
    loading,
    getSelectProps,
    getConfirmDialogProps,
    confirmDialogOpen,
  }
}
